import * as React from 'react';
import { ReactNode, useState } from 'react';
import Avatar from 'react-avatar';
import { styled } from '../../theme';
import { observer } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ENV } from '../../env';
import { NotificationsLayerWidget } from './NotificationsLayerWidget';
import { Notification } from '../../stores/NotificationStore';

const logo = require('../../../media/journeyapps-icon-light-bg.png');

export interface HeaderWidgetProps {
  title?: ReactNode;
  email?: string | null;
  name?: string;
  profileRef?: React.MutableRefObject<any>;
  onProfileClick?: (event: React.MouseEvent) => any;
  notifications: Notification[];
}

export const HeaderWidget: React.FC<HeaderWidgetProps> = observer((props) => {
  const [showNotifications, setShowNotifications] = useState(false);

  const getTitle = (): JSX.Element => {
    if (React.isValidElement(props.title)) {
      return props.title;
    }
    return <S.Title>{props.title}</S.Title>;
  };

  return (
    <S.Container>
      <S.Branding
        onClick={() => {
          window.location.replace(`${ENV.ACCOUNTS_PORTAL_URL}/admin`);
        }}
      >
        <S.Logo src={logo} />
      </S.Branding>
      {getTitle()}
      <S.NotificationContainer
        onClick={() => {
          setShowNotifications(!showNotifications);
        }}
      >
        <S.NotificationBell icon="bell" />
        {props.notifications.length > 0 ? <S.NotificationIndicator></S.NotificationIndicator> : null}
        {showNotifications ? (
          <NotificationsLayerWidget
            notifications={props.notifications}
            close={() => {
              setShowNotifications(false);
            }}
          />
        ) : null}
      </S.NotificationContainer>
      <S.User
        ref={props.profileRef}
        onClick={(e) => {
          props.onProfileClick?.(e);
        }}
      >
        <S.AvatarCircle email={props.email || ''} name={props.name} maxInitials={2} />
      </S.User>
    </S.Container>
  );
});

export const HEADER_HORIZONTAL_PADDING = 30;
const widthAvatar = 30;
namespace S {
  export const Container = styled.div`
    display: flex;
    align-items: center;
    padding-left: ${HEADER_HORIZONTAL_PADDING}px;
    padding-right: ${HEADER_HORIZONTAL_PADDING}px;
    background-image: linear-gradient(
      -90deg,
      ${(p) => p.theme.header.background},
      ${(p) => p.theme.header.backgroundDarker}
    );
    min-height: 64px;
    border-bottom: ${(p) => p.theme.header.borderColor} solid 1px;
    width: 100%;
    user-select: none;
  `;

  export const Branding = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `;

  export const Logo = styled.img`
    height: 42px;
    -webkit-user-drag: none;
  `;

  export const Title = styled.div`
    flex-grow: 1;
    font-size: 21px;
    font-weight: bold;
    padding-left: 16px;
    color: ${(p) => p.theme.header.color};
  `;

  export const NotificationContainer = styled.div`
    position: relative;
    cursor: pointer;
  `;

  export const NotificationBell = styled(FontAwesomeIcon)`
    font-size: 25px;
    color: ${(p) => p.theme.header.color};
    padding: 5px;
  `;

  const IndicatorSize = 12;

  export const NotificationIndicator = styled.div`
    position: absolute;
    background: ${(p) => p.theme.button.primary.background};
    width: ${IndicatorSize}px;
    height: ${IndicatorSize}px;
    border-radius: ${IndicatorSize / 2}px;
    top: 5px;
    left: 5px;
    border: solid 2px ${(p) => p.theme.header.background};
  `;

  export const AvatarCircle = styled(Avatar)`
    max-height: ${widthAvatar}px;
    max-width: ${widthAvatar}px;
    border-radius: ${widthAvatar / 2}px;
  `;

  export const User = styled.div`
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.5);
    font-size: 18px;
    margin-left: 8px;
    margin-right: 8px;
    cursor: pointer;
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
  `;
}
